class FormOverridesManager {
  constructor() {
    this.forms = [];
  }

  async registerForm(formId) {
    const formDOMElement = await this.getDOMRoot(formId);
    const form = {
      id: formId,
      element: formDOMElement,
      shadowRoot: formDOMElement.shadowRoot,
    };
    this.forms.push(form);
    this.fixRequiredAttributes(formId);
    this.fixLabelAssociations(formId);
    this.fixOptionListRoles(formId);
    return form;
  }

  addStyleOverride(formId, styles) {
    const form = this.getForm(formId);
    const styleTag = document.createElement('style');
    styleTag.innerHTML = styles;
    form.shadowRoot.appendChild(styleTag);

    // The Shopify Forms app injects its own <style> elements into the shadow root
    // This observer keeps our tag last in the cascade
    const observer = new MutationObserver(() => {
      if (form.shadowRoot.lastElementChild !== styleTag) {
        form.shadowRoot.appendChild(styleTag);
      }
    });
    observer.observe(form.shadowRoot, { childList: true });
  }

  getForm(formId) {
    const form = this.forms.find((form) => form.id === formId);
    if (!form) throw new Error(`Form with id ${formId} has not been registered`);
    return form;
  }

  fixRequiredAttributes(formId) {
    const form = this.getForm(formId);
    const formLabels = form.shadowRoot.querySelectorAll('label, legend');
    [...formLabels]
      .filter((label) => label.textContent.includes('*'))
      .map((label) => {
        const id = label.id;
        const input = form.shadowRoot.querySelector(`[aria-labelledby="${id}"]`);

        if (input) {
          input.required = true;
          return;
        }

        const fs = label.closest('fieldset');
        if (fs) {
          fs.querySelectorAll('input[type=radio], input[type=checkbox]').forEach((i) => {
            i.required = true;
          });
        }
      });
  }

  fixLabelAssociations(formId) {
    const form = this.getForm(formId);
    const { shadowRoot } = form;

    // The app renders each label's `for` pointing at the label's own id instead of
    // the field's id. Inputs are rescued by an `aria-labelledby` the app also adds,
    // but the message textarea doesn't get one, leaving it with no accessible name.
    shadowRoot.querySelectorAll('textarea:not([aria-labelledby])').forEach((textarea) => {
      const label = textarea.nextElementSibling;
      if (label?.tagName === 'LABEL' && label.id) {
        textarea.setAttribute('aria-labelledby', label.id);
      }
    });

    // Custom "select" dropdown triggers render a visible <p> caption that's never
    // wired up to the button, so the button has no accessible name at all. The
    // trigger's aria-labelledby also includes the toggle-text span so the current
    // selection reads as part of the name, same as a native <select>.
    shadowRoot.querySelectorAll('[class*="_selectContainer_"] > p[class*="_label_"]').forEach((caption, i) => {
      const button = caption.nextElementSibling;
      if (button?.tagName !== 'BUTTON') return;
      if (!caption.id) caption.id = `select-label-${formId}-${i}`;

      const valueSpan = button.querySelector('[class*="_selectToggleText_"]');
      if (valueSpan && !valueSpan.id) valueSpan.id = `select-value-${formId}-${i}`;

      button.setAttribute('aria-labelledby', valueSpan ? `${caption.id} ${valueSpan.id}` : caption.id);
    });
  }

  fixOptionListRoles(formId) {
    const form = this.getForm(formId);
    const { shadowRoot } = form;

    // Option panels already carry aria-selected on the right item, but the panel
    // itself has no role, so role="option" has no listbox to belong to and VoiceOver
    // won't read selection/position state. The app likely rebuilds this panel each
    // time it opens, so keep patching it in rather than fixing it once.
    const patchPanels = () => {
      shadowRoot.querySelectorAll('[id$="-selector-dropdown"]:not([role])').forEach((panel) => {
        panel.setAttribute('role', 'listbox');
      });

      // aria-selected="false" is read aloud as "not selected" on every unselected
      // option. Per the APG listbox pattern, only the selected option should carry
      // aria-selected at all.
      shadowRoot.querySelectorAll('[role="option"][aria-selected="false"]').forEach((option) => {
        option.removeAttribute('aria-selected');
      });

      // Each option is wrapped in an unroled div, breaking the listbox's direct
      // containment of its options. VoiceOver falls back to reading these as
      // "menu item" instead of "option" with selection state. Marking the wrapper
      // presentational removes it from the accessibility tree so the option is
      // exposed as a direct child of the listbox again.
      shadowRoot.querySelectorAll('[role="listbox"] > div:not([role])').forEach((wrapper) => {
        wrapper.setAttribute('role', 'presentation');
      });
    };

    patchPanels();
    const observer = new MutationObserver(patchPanels);
    observer.observe(shadowRoot, { childList: true, subtree: true });
  }

  async getDOMRoot(formId) {
    const selector = `[data-forms-id="${formId}"] #app-embed`;

    // First check if element already exists
    const existingElement = document.querySelector(selector);
    if (existingElement) {
      return existingElement;
    }

    // If not, wait for it to be added
    return new Promise((resolve) => {
      const observer = new MutationObserver((mutations) => {
        for (const mutation of mutations) {
          if (mutation.addedNodes.length) {
            for (const node of mutation.addedNodes) {
              // Skip if not an element node
              if (node.nodeType !== Node.ELEMENT_NODE) continue;

              // Check if the added node matches our selector
              if (node.matches && node.matches(selector)) {
                observer.disconnect();
                resolve(node);
                return;
              }

              // Check if the added node contains our target element
              if (node.querySelector) {
                const targetElement = node.querySelector(selector);
                if (targetElement) {
                  observer.disconnect();
                  resolve(targetElement);
                  return;
                }
              }
            }
          }
        }
      });

      observer.observe(document.body, {
        childList: true,
        subtree: true,
      });
    });
  }
}

const styles = `
h2 {
  font-family: var(--font-heading-family) !important;
  text-transform: uppercase !important;
  font-size: 3.2rem !important;
}

legend, label, input, span {
  font-family: var(--font-body-family) !important;
  font-size: 1.8rem !important;
  color: var(--dark-purple) !important;
}

input[type="text"],
input[type="email"],
input[type="tel"],
textarea {
  box-shadow: 0 0 0 2px var(--dark-purple) !important;
  border-radius: 1.2rem !important;
  background-color: transparent !important;
}

[class*="formPhoneInputContainer"] {
  display: block !important;
}

.phone-country-selector {
  display: none !important;
}

fieldset, textarea {
  margin-bottom: 1.4rem !important;
}

textarea+label,
div:has(>textarea)::before {
  background-color: transparent !important;
  border-radius: 1.2rem !important;
}

[data-sizing="form-wrapper"] {
  width: 100% !important;
  max-width: none !important;
}

input[type="radio"],
input[type="checkbox"] {
  --size: 1.3rem;
  width: var(--size);
  height: var(--size);
  transform: translateY(-.2rem);
  border-color: var(--dark-purple) !important;
  border-size: 2px !important;
  background-color: transparent !important;
}

button[type="submit"] {
  font-family: var(--font-heading-family) !important;
  text-transform: uppercase !important;
  font-size: 2.1rem !important;
  padding-top: 2.1rem !important;
}

[class*="formDisclaimer"] {
  display: none;
}
`;

const contactFormStyles = `
h2 {
  font-family: var(--font-heading-family) !important;
  font-size: 4rem !important;
  margin-bottom: 2rem !important;
  color: var(--yellow) !important;
  letter-spacing: 1px !important;
  font-weight: 400 !important;
}

div[class*="textBody"] p {
  font-family: var(--font-body-family);
}

legend, label, input, span, p[class*="label"] {
  font-family: var(--font-body-family) !important;
  font-size: 1.8rem !important;
  color: var(--pink) !important;
}

p[class*="label"] {
  font-weight: 700 !important;
  padding-top: 1rem;
}

[class*="formPhoneInputContainer"] {
  display: block !important;
}

.phone-country-selector {
  display: none !important;
}

fieldset, textarea {
  margin-bottom: 1.4rem !important;
}

input[type="text"][class*="formInputField"],
input[type="email"],
input[type="tel"],
textarea {
  box-shadow: 0 0 0 2px var(--pink) !important;
  border-radius: 1.2rem !important;
  background-color: var(--purple) !important;
  -webkit-appearance: auto !important;
  color: var(--pink) !important;
  -webkit-text-fill-color: var(--pink) !important;
  line-height: 1.5 !important;
  min-height: 1.8rem !important;
  font-size: 16px !important;
  -webkit-transform: translateZ(0) !important;
  transform: none !important;
  text-indent: 0 !important;
}

textarea+label,
div:has(>textarea)::before {
  background-color: transparent !important;
  border-radius: 1.2rem !important;
}

[data-sizing="form-wrapper"] {
  width: 100% !important;
  max-width: 800px !important;
  margin-inline: auto !important;
  box-sizing: border-box !important;
}

[data-testid="app-router"] {
  display: flex !important;
  flex-direction: column !important;
  width: 100% !important;
}

form {
  display: flex !important;
  flex-direction: column !important;
  gap: 2rem !important;
  width: 100% !important;
  max-width: none !important;
  box-sizing: border-box !important;
}

button[type="submit"] {
  font-family: var(--font-heading-family) !important;
  text-transform: uppercase !important;
  font-size: 2.1rem !important;
  padding-top: 2.1rem !important;
  background-color: var(--yellow) !important;
  color: var(--dark-purple) !important;
}

button[class*="selectToggle"] {
  outline: 2px solid var(--pink) !important;
  border-radius: var(--buttons-radius);
  background-color: var(--purple) !important;
  line-height: 1.5 !important;
}

div[class*="dropdownContainer"] {
  border: 2px solid var(--dark-purple);
  font-family: var(--font-body-family);
  color: var(--dark-purple) !important;
  background-color: var(--purple) !important;
}

div[class*="dropdownContainer"] * {
  color: var(--dark-purple) !important;
  background-color: #FFF !important;
}

button[class*="formFileInputField"] {
  border-color: var(--pink) !important;
  border-width: 2px;
  background-color: var(--dark-purple) !important;
}

[class*="formFileInputButton"] {
  background-color: var(--yellow) !important;
  font-family: var(--font-block) !important;
  font-size: 1.8rem !important;
  color: var(--dark-purple) !important;
  font-weight: 700 !important;
  text-transform: uppercase !important;
  padding: 1rem 2rem !important;
}

#quality-concern-message {
  display: none;
  background-color: var(--light-pink);
  border: 2px solid var(--pink);
  border-radius: 1.2rem;
  padding: 1.6rem 2rem;
  margin-bottom: 1.4rem;
  text-align: left !important;
}

#quality-concern-message.is-visible {
  display: block;
}

#quality-concern-message p {
  font-family: var(--font-body-family) !important;
  font-size: 1.6rem !important;
  color: var(--dark-purple) !important;
  margin-bottom: 1rem !important;
}

#quality-concern-message ul {
  padding-left: 2rem;
  margin: 0;
}

#quality-concern-message li {
  font-family: var(--font-body-family) !important;
  font-size: 1.6rem !important;
  color: var(--dark-purple) !important;
  margin-bottom: 0.4rem !important;
}

#quality-concern-message .quality-example-link {
  color: var(--dark-purple) !important;
  text-decoration: underline !important;
  cursor: pointer !important;
}

#quality-concern-message .quality-example-link:focus-visible {
  outline: 2px solid var(--yellow);
  outline-offset: 2px;
  border-radius: 2px;
}

[class*="_formFileInput"]:has(label[for*="carton_code"]) {
  display: none;
}

[class*="_formFileInput"]:has(label[for*="carton_code"]).show {
  display: block;
}
`;

const QUALITY_CONCERN_EXAMPLE_IMAGE_URL =
  'https://cdn.shopify.com/s/files/1/0715/5181/1873/files/Nellie_s_Date_Example.jpg?v=1779899675';

function setupQualityConcernMessage(form) {
  const { shadowRoot } = form;

  // Message block
  const msgEl = document.createElement('div');
  msgEl.id = 'quality-concern-message';
  msgEl.setAttribute('aria-live', 'polite');
  msgEl.innerHTML = `
    <p>If you're contacting us about a potential quality concern, please include the details below in your submission so we can look into it and make things right. We hold our products to strict quality standards and stand behind every carton.</p>
    <ul>
      <li>Use by date &amp; numbers printed in black ink along the short side of the carton (<a href="#" class="quality-example-link" aria-label="View example image of carton date location">example</a>)</li>
      <li>Carton color and size (ex: Purple carton, Large eggs)</li>
      <li>Store and location where you purchased the eggs</li>
      <li>Any photos of the eggs/carton you have</li>
    </ul>
  `;

  // Lightbox
  const lightbox = document.createElement('div');
  lightbox.id = 'quality-lightbox';
  lightbox.setAttribute('role', 'dialog');
  lightbox.setAttribute('aria-modal', 'true');
  lightbox.setAttribute('aria-label', 'Carton date example');
  lightbox.innerHTML = `
    <div class="quality-lightbox__inner">
      <button class="quality-lightbox__close" aria-label="Close lightbox">&times;</button>
      <img class="quality-lightbox__img" src="${QUALITY_CONCERN_EXAMPLE_IMAGE_URL}" alt="Example showing where to find the use by date printed on the carton" />
    </div>
  `;
  document.body.appendChild(lightbox);

  const lightboxStyles = document.createElement('style');
  lightboxStyles.textContent = `
    #quality-lightbox {
      display: none;
      position: fixed;
      inset: 0;
      background-color: rgba(63, 24, 66, 0.92);
      z-index: 9999;
      align-items: center;
      justify-content: center;
      padding: 2rem;
    }
    #quality-lightbox.is-open {
      display: flex;
    }
    .quality-lightbox__inner {
      position: relative;
      max-width: 90vw;
      max-height: 90vh;
    }
    .quality-lightbox__img {
      display: block;
      max-width: 100%;
      max-height: 85vh;
      object-fit: contain;
      border-radius: 0.8rem;
    }
    .quality-lightbox__close {
      position: absolute;
      top: -1.6rem;
      right: -1.6rem;
      width: 3.2rem;
      height: 3.2rem;
      border-radius: 50%;
      border: none;
      background: var(--yellow);
      color: var(--dark-purple);
      font-size: 2rem;
      line-height: 1;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    .quality-lightbox__close:focus-visible {
      outline: 2px solid var(--yellow);
      outline-offset: 2px;
    }
  `;
  document.head.appendChild(lightboxStyles);

  // Lightbox open/close
  let lightboxTrigger = null;

  const openLightbox = (trigger) => {
    lightboxTrigger = trigger;
    lightbox.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    lightbox.querySelector('.quality-lightbox__close').focus();
  };

  const closeLightbox = () => {
    lightbox.classList.remove('is-open');
    document.body.style.overflow = '';
    lightboxTrigger?.focus();
    lightboxTrigger = null;
  };

  msgEl.addEventListener('click', (e) => {
    if (e.target.classList.contains('quality-example-link')) {
      e.preventDefault();
      openLightbox(e.target);
    }
  });

  lightbox.addEventListener('click', (e) => {
    if (e.target === lightbox || e.target.classList.contains('quality-lightbox__close')) {
      closeLightbox();
    }
  });

  document.addEventListener('keydown', (e) => {
    if (!lightbox.classList.contains('is-open')) return;

    if (e.key === 'Escape') {
      closeLightbox();
      return;
    }

    // Trap focus within the lightbox
    if (e.key === 'Tab') {
      const focusable = [
        ...lightbox.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'),
      ];
      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  });

  // Show/hide message based on dropdown value
  const isQualityConcern = (text) => text.toLowerCase().includes('quality');

  const getInquiryToggleText = () => {
    const toggle = shadowRoot.querySelector('[aria-labelledby*="type_of_inquiry-selector-value"]');
    return toggle?.querySelector('[class*="selectToggleText"]')?.textContent ?? '';
  };

  const updateVisibility = () => {
    const cartonCodeEls = shadowRoot.querySelectorAll('[class*="_formFileInput"]:has(label[for*="carton_code"])')
    msgEl.classList.toggle('is-visible', isQualityConcern(getInquiryToggleText()));
    cartonCodeEls.forEach(cartonCodeEl => {
      cartonCodeEl.classList.toggle('show');
    });
  };

  const watchToggleText = () => {
    const toggleTextSpan = shadowRoot.querySelector('[aria-labelledby*="type_of_inquiry-selector-value"] [class*="selectToggleText"]');
    if (toggleTextSpan) {
      const observer = new MutationObserver(updateVisibility);
      observer.observe(toggleTextSpan, { childList: true, characterData: true, subtree: true });
    }
  };

  shadowRoot.addEventListener('change', (e) => {
    if (e.target.matches('select')) {
      msgEl.classList.toggle('is-visible', isQualityConcern(e.target.value));
    }
  });

  // Inject message after the Type of Inquiry field
  const injectMessage = () => {
    const inquiryToggle = shadowRoot.querySelector('[aria-labelledby*="type_of_inquiry-selector-value"]');

    if (inquiryToggle) {
      const fieldContainer = inquiryToggle.parentElement;
      if (fieldContainer) {
        fieldContainer.insertAdjacentElement('afterend', msgEl);
        return;
      }
    }

    // Fallback: insert before the submit button
    const submitBtn = shadowRoot.querySelector('button[type="submit"]');
    submitBtn?.insertAdjacentElement('beforebegin', msgEl);
  };

  // Wait for shadow root form fields to render, then inject and start watching
  const init = () => {
    injectMessage();
    watchToggleText();
  };

  if (shadowRoot.querySelector('label, legend')) {
    init();
  } else {
    const observer = new MutationObserver(() => {
      if (shadowRoot.querySelector('label, legend')) {
        observer.disconnect();
        init();
      }
    });
    observer.observe(shadowRoot, { childList: true, subtree: true });
  }
}

const formManager = new FormOverridesManager();
const farmerRecruitmentFormId = 'forms-root-751985';
const contactFormId = 'forms-root-776895';

formManager.registerForm(farmerRecruitmentFormId).then((form) => {
  // in here we get access to form.id, form.shadowRoot (for any custom DOM tweaks),
  // form.element (container thats outside of shadow root), and we can call
  // formManager.addStyleOverride(form.id, styles) where styles is a CSS string
  formManager.addStyleOverride(form.id, styles);
});

formManager.registerForm(contactFormId).then((form) => {
  formManager.addStyleOverride(form.id, contactFormStyles);
  setupQualityConcernMessage(form);

  const container = form.element.parentElement.parentElement.parentElement;
  if (container) {
    container.setAttribute('id', 'contact-form-container');
    container.style.maxWidth = '760px';
    container.style.marginInline = 'auto';
  }

  const sectionContainer = container?.parentElement;
  if (sectionContainer) {
    sectionContainer.setAttribute('id', 'contact-form-section');
    sectionContainer.style.maxWidth = '760px';
    sectionContainer.style.marginInline = 'auto';
  }

  const sectionStyles = document.createElement('style');
  sectionStyles.textContent = `
    #contact-form-section {
      padding: 3rem;
      box-sizing: border-box;
    }
  `;
  document.head.appendChild(sectionStyles);

  form.shadowRoot.querySelector('form').addEventListener('submit', () => {
    window.dataLayer.push({
      event: 'contact_click',
    });
  });
});
